import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const publicUrl = process.env.PUBLIC_URL || '';

function PhotoHalf({ src, letter }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="flex h-full min-h-[100px] items-center justify-center bg-gradient-to-br from-[#FFD2CD]/25 to-[#FAF7F2] text-[clamp(1rem,3vw,1.35rem)] font-semibold text-[#FF5E4C]">
        {letter}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className="h-full w-full min-h-0 object-cover object-center"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

/**
 * Team / story photo card. About portraits live under `public/assets/` (see imageSrcs below).
 * Joey & Chandler: `joechandler.jpeg`. Pairs in one card: pass two URLs + dualLetters.
 */
function TeamPhotoCard({ name, initials, imageSrcs, dualLetters, children, className = '' }) {
  const hasDual = imageSrcs && imageSrcs.length > 1;
  const hasSingle = imageSrcs && imageSrcs.length === 1;
  const [singleFailed, setSingleFailed] = useState(false);

  const letters = dualLetters && dualLetters.length >= 2 ? dualLetters : ['?', '?'];
  const showInitials = !imageSrcs || imageSrcs.length === 0 || (hasSingle && singleFailed);

  return (
    <div
      className={`rounded-[16px] overflow-hidden border border-[#E5E3DF] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)] ${className}`.trim()}
    >
      <div className="relative aspect-square w-full overflow-hidden bg-gradient-to-br from-[#FFF0ED] to-[#FAF7F2]">
        {!showInitials && hasSingle ? (
          <img
            src={imageSrcs[0]}
            alt=""
            className="absolute inset-0 h-full w-full min-h-0 object-cover object-center"
            loading="lazy"
            onError={() => setSingleFailed(true)}
          />
        ) : !showInitials && hasDual ? (
          <div className="absolute inset-0 grid min-h-0 grid-cols-2 gap-0">
            <div className="relative h-full min-h-0 overflow-hidden">
              <PhotoHalf src={imageSrcs[0]} letter={letters[0]} />
            </div>
            <div className="relative h-full min-h-0 overflow-hidden">
              <PhotoHalf src={imageSrcs[1]} letter={letters[1]} />
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#FFD2CD]/35 to-[#FAF7F2]">
            <span className="text-[clamp(1.15rem,3.8vw,1.65rem)] font-semibold tracking-tight text-[#FF5E4C]">
              {initials}
            </span>
          </div>
        )}
      </div>
      <div className="p-5 md:p-6 lg:p-5 border-t border-[#E5E3DF] bg-[#FAFAFA]">
        <p className="text-[16px] md:text-[17px] lg:text-[15px] font-medium text-black mb-2 lg:mb-1.5">{name}</p>
        {children}
      </div>
    </div>
  );
}

const About = () => {
  const navigate = useNavigate();

  const pClass = 'text-[15px] md:text-[16px] text-[#5c5c5c] leading-[1.65]';
  const h2Class = 'text-[20px] md:text-[22px] font-medium text-black tracking-[-0.02em] mt-10 md:mt-14 mb-4 md:mb-5';
  const listClass = 'list-disc pl-5 space-y-2 ' + pClass;

  const joeyChandlerImg = `${publicUrl}/assets/joechandler.jpeg`;
  const sanyaHarshImg = `${publicUrl}/assets/${encodeURIComponent('sanya and harsh.jpg')}`;
  const aromalImg = `${publicUrl}/assets/${encodeURIComponent('aromal image.jpeg')}`;
  const anujImg = `${publicUrl}/assets/${encodeURIComponent('anuj image.jpeg')}`;
  const dollyImg = `${publicUrl}/assets/dolly.jpg`;
  const praveenImg = `${publicUrl}/assets/Praveen.jpg`;
  const arunImg = `${publicUrl}/assets/Arun.jpg`;

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <div className="mx-auto px-5 sm:px-8 md:px-10 lg:px-16 xl:px-20 2xl:px-24 pb-16 md:pb-24 lg:pb-28 pt-4 md:pt-8 lg:pt-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8 md:mb-10">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hover:bg-black/5 rounded-full p-1 -ml-1 shrink-0"
            aria-label="Go back"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_about_back)">
                <path d="M5 12H19" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 12L11 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 12L11 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_about_back">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
          <h1 className="text-[22px] md:text-[26px] font-medium text-black tracking-[-0.03em]">About us</h1>
        </div>

        <article className="bg-white rounded-[20px] shadow-[0_4px_4px_rgba(0,0,0,0.04)] px-6 sm:px-8 md:px-10 lg:px-14 xl:px-16 py-8 md:py-12 lg:py-14">
          {/* Opening */}
          <header className="mb-8 md:mb-10">
            <h2 className="text-[24px] md:text-[28px] font-medium text-black tracking-[-0.03em] leading-[1.2] mb-3">
              Not Just Pet-Friendly. Pet-First.
            </h2>
            <p className="text-[16px] md:text-[17px] text-[#78736A] font-medium mb-6">
              Built from real journeys, for real pet lives.
            </p>
          </header>

          <div className="space-y-5">
            <p className={pClass}>
              Book For Paws was born out of lived experiences — countless journeys with our dogs, searching for places
              that didn&apos;t just allow them, but truly welcomed them.
            </p>
            <p className={pClass}>
              We are a pet-first travel and experiences platform — curating stays and events where pets are not an
              afterthought, but the starting point.
            </p>
            <p className={pClass}>
              Every place you see here has been experienced, tested, and approved by those who matter most — the pets
              themselves.
            </p>
            <p className={`${pClass} font-medium text-black`}>
              Because pet-friendly isn&apos;t a standard. Comfort is.
            </p>
          </div>

          {/* Section 2 & 3 content — no section titles */}
          <div className="mt-10 md:mt-12 space-y-5">
            <p className={pClass}>
              Every stay and event is lived, tested, and verified. We bring together a curated world of:
            </p>
            <ul className={listClass}>
              <li>Pet-friendly stays that offer freedom, safety, and real experiences</li>
              <li>Trainer-led events designed for both pets and their people</li>
              <li>Verified spaces where policies match reality</li>
            </ul>
            <p className={pClass}>
              Everything you see is filtered through one simple lens — Would we take our own dogs here? If the answer
              isn&apos;t a confident yes, it doesn&apos;t make it.
            </p>
          </div>

          <div className="mt-10 md:mt-12 space-y-4">
            <p className={`${pClass} font-medium text-black`}>Freedom, safety, and honesty — always.</p>
            <ul className={listClass}>
              <li>Pets deserve experiences, not restrictions</li>
              <li>Trust is built through real verification, not assumptions</li>
              <li>Travel should feel effortless — for both pets and their people</li>
            </ul>
          </div>

          <h2 className={h2Class}>The Pack Behind It</h2>
          <p className={`${pClass} mb-6 md:mb-8`}>Led by instinct. Built by intention.</p>
          <div className="flex flex-col gap-10 md:gap-12">
            <div className="lg:max-w-[360px]">
              <TeamPhotoCard
                name="Joey & Chandler"
                initials="J & C"
                imageSrcs={[joeyChandlerImg]}
              >
                <p className={`${pClass} mb-2`}>The reason this exists.</p>
                <p className={pClass}>
                  The lens through which every decision is made. Explorers, testers, and the final approval on everything
                  you see.
                </p>
              </TeamPhotoCard>
            </div>
            <div className="lg:max-w-[360px]">
              <TeamPhotoCard
                name="Sanya & Harsh"
                initials="S & H"
                imageSrcs={[sanyaHarshImg]}
              >
                <p className={`${pClass} mb-2`}>Building the world around them.</p>
                <p className={pClass}>
                  Turning lived experiences into a platform that works — for pets and their people.
                </p>
              </TeamPhotoCard>
            </div>
          </div>

          <h2 className={h2Class}>Built Quietly, Runs Seamlessly</h2>
          <p className={`${pClass} mb-4 md:mb-6`}>The backbone you don&apos;t see, but always feel.</p>
          <div>
            <p className="text-[16px] md:text-[17px] font-medium text-black mb-2">Aromal &amp; Anuj</p>
            <p className={`${pClass} mb-4 md:mb-6`}>
              Designing and building the systems that make every experience smooth, reliable, and effortless.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 lg:max-w-[600px] lg:gap-6">
              <TeamPhotoCard name="Aromal" initials="AR" imageSrcs={[aromalImg]} />
              <TeamPhotoCard name="Anuj" initials="AN" imageSrcs={[anujImg]} />
            </div>
          </div>

          <h2 className={h2Class}>Seen Through Their Eyes</h2>
          <p className={`${pClass} mb-6 md:mb-8`}>Nothing staged. Everything real.</p>
          <div className="grid grid-cols-1 gap-8 md:gap-10 lg:gap-12 lg:max-w-[920px] items-start">
            <div className="lg:max-w-[360px]">
              <TeamPhotoCard name="Dolly" initials="D" imageSrcs={[dollyImg]}>
                <p className={pClass}>Capturing moments exactly as they are — raw, real, and honest.</p>
              </TeamPhotoCard>
            </div>
            <div className="min-w-0">
              <p className="text-[16px] md:text-[17px] font-medium text-black mb-2">Praveen &amp; Arun</p>
              <p className={`${pClass} mb-4 md:mb-6`}>
                Bringing movement to stories — documenting experiences the way they are truly lived.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 lg:max-w-[600px] lg:gap-6">
                <TeamPhotoCard name="Praveen" initials="P" imageSrcs={[praveenImg]} />
                <TeamPhotoCard name="Arun" initials="A" imageSrcs={[arunImg]} />
              </div>
            </div>
          </div>

          <p className={`${pClass} mt-12 md:mt-14 pt-8 border-t border-[#E5E3DF] text-center`}>
            Discover pet-friendly stays and curated dog events across India — verified by pets, trusted by pet parents.
          </p>
        </article>
      </div>
    </div>
  );
};

export default About;
